import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidV4 } from 'uuid';
import { useForm as useHookForm, FormProvider } from 'react-hook-form';
import { BenchmarkForm } from '../../components/BenchmarkMetadata/components/BenchmarkForm/BenchmarkForm';
import { useTable, useTimeWindow } from './hooks';
import { Footer, TableTabbed } from 'components';
import {
  BenchmarkScreen,
  useBenchmarkDetails,
  useTWNavigation,
  useTWScreenParams,
  useTimeWindows,
} from 'hooks';
import { useYupValidationResolver } from '../../helpers/form';
import { BenchmarkAssetWeightedTimeWindow } from './BenchmarkAssetWeightedTimeWindow';
import { DevTool } from '@hookform/devtools';
import { showSuccess } from 'helpers/showSuccess';
import { useSubmit } from 'hooks/useSubmit';
import {
  AssetWeighted,
  AssetWeightedLineItem,
  AssetWeightedTimeWindow,
} from 'types/AssetWeighted';
import { getBody } from './BenchmarkAssetWeightedHelpers';
import { BenchmarkMetadata } from 'components/BenchmarkMetadata';
import { SmallDisplay } from './components/SmallDisplay';
import { DefaultDisplay } from './components/DefaultDisplay';
import { TimeWindows, TimeWindowsEditTitle } from 'components/TimeWindow';
import { AssetWeightedTimeWindowHeader } from './components/TimeWindowHeader';
import {
  assetWeightedTimeWindowShape,
  assetWeightedValidationSchema,
} from './AssetWeightedValidationScheme';
import { getToDates } from 'helpers/timeWindow/getToDates';
import { BenchmarkFormTitle } from 'components/BenchmarkMetadata/components/BenchmarkForm/BenchmarkFormStyles';
import { BenchmarkTypes } from 'types/Benchmark';
import { useProblemsHandler } from 'modules/BenchmarkForm/hooks/useProblemsHandler';
import { LoadingWrapper } from 'components/LoadingWrapper/LoadingWrapper';

type Props = {};

export const BenchmarkAssetWeighted: React.FC<Props> = () => {
  const [isFormLoadComplete, setIsFormLoadComplete] = useState(false);
  const resolver = useYupValidationResolver(assetWeightedValidationSchema());
  const methods = useHookForm<any>({
    resolver,
    defaultValues: {
      baseCurrencyCode: 'USD',
    },
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    reset,
    setValue,
    setError,
  } = methods;

  const { timeWindows, timeWindowsFilled } =
    useTimeWindows<AssetWeightedTimeWindow>({
      setValue,
      control,
      timeWindowSchema: assetWeightedTimeWindowShape,
    });

  const {
    benchmark,
    refetch,
    isLoading: isLoadingBenchmark,
  } = useBenchmarkDetails<AssetWeighted>();

  const { isTimeWindowsScreen, addTimeWindow, benchmarkScreen } =
    useTWScreenParams(benchmark?.id);

  const handleTWNavigation = useTWNavigation(benchmarkScreen);

  const tableProps = useTable(timeWindows);

  const {
    onSubmit,
    isSuccess,
    isLoading: isSubmitting,
    errors: problemDetails,
  } = useSubmit({
    originalValues: benchmark,
    originalTimeWindows: timeWindows,
    getBody,
  });

  useProblemsHandler(setError, problemDetails);

  useEffect(() => {
    if (isSuccess) {
      if (benchmark?.id) {
        showSuccess('Benchmark was successfully saved');
        refetch();
        handleTWNavigation();
        window.scrollTo(0, 0);
      }
    }
  }, [isSuccess, benchmark?.id]);

  const resetData = useCallback(() => {
    reset();
    // TODO: Update weighting and return component labels
    if (benchmark) {
      let values = {
        ...benchmark,
        timeWindows: getToDates(
          benchmark?.timeWindows?.map((item: any) => {
            return {
              toggleId: uuidV4(),
              fromDate: new Date(item.fromDate),
              assetSelectionsData: false,
              lineItems: item.lineItems.map(
                (lineItem: AssetWeightedLineItem) => {
                  return {
                    returnComponent:
                      lineItem.returnComponentBenchmarkId ||
                      lineItem.returnComponentResourceId,
                    returnComponentName: lineItem.returnComponentName,
                    weightingComponent:
                      lineItem.weightingComponentBenchmarkId ||
                      lineItem.weightingComponentResourceId,
                    weightingComponentName: lineItem.weightingComponentName,
                    isLagged: lineItem.isLagged,
                    customLagDate: lineItem.customLagDate,
                    defaultLagDate: benchmark.defaultLagDate,
                  };
                }
              ),
            };
          })
        ),
      };
      reset(values);
    }
  }, [benchmark, reset]);

  useEffect(() => {
    resetData();
  }, [resetData]);

  const tableTabbedProps = {
    ...tableProps,
    useTimeWindow,
    handleSubmit,
    isLoading: isSubmitting || isLoadingBenchmark,
    onSubmit,
    benchmarkName: benchmark?.name,
  };

  const renderContent = useMemo(() => {
    if (isLoadingBenchmark) return null;

    if (isTimeWindowsScreen || addTimeWindow) {
      return (
        <TimeWindows
          benchmarkType={BenchmarkTypes.AssetWeighted}
          timeWindowsFilled={timeWindowsFilled}
          benchmarkId={benchmark?.id}
          TimeWindowComponent={BenchmarkAssetWeightedTimeWindow}
          HeaderComponent={AssetWeightedTimeWindowHeader}
          defaultValues={{
            fromDate: new Date(),
            toggleId: uuidV4(),
            lineItems: [],
          }}
        />
      );
    }

    return <TableTabbed {...tableTabbedProps} benchmarkId={benchmark?.id} />;
  }, [
    benchmark?.id,
    isTimeWindowsScreen,
    isLoadingBenchmark,
    tableTabbedProps,
  ]);

  return (
    <>
      <LoadingWrapper isLoading={isLoadingBenchmark && isFormLoadComplete}>
        <TimeWindowsEditTitle isTimeWindowsScreen={isTimeWindowsScreen} />
        {benchmark?.id && !isSubmitting ? (
          <BenchmarkMetadata
            benchmark={benchmark}
            onConfirm={refetch}
            isTimeWindowsScreen={isTimeWindowsScreen}
          >
            <SmallDisplay benchmark={benchmark} />
            <DefaultDisplay benchmark={benchmark} />
          </BenchmarkMetadata>
        ) : null}
        <FormProvider {...methods}>
          {benchmark === undefined ? (
            <div style={{ padding: 20 }}>
              <BenchmarkFormTitle>
                Add New Asset Weighted Benchmark
              </BenchmarkFormTitle>
              <BenchmarkForm
                benchmark={benchmark}
                benchmarkType={BenchmarkTypes.AssetWeighted}
                onLoadComplete={() => setIsFormLoadComplete(true)}
              />
            </div>
          ) : null}
          {renderContent}
          <Footer
            showReturnHome={benchmarkScreen === BenchmarkScreen.editBenchmark}
            onSave={handleSubmit((value: any) => {
              onSubmit({
                ...value,
                onSaveSuccess: isSuccess,
              });
            })}
            onDiscardChanges={() => {
              resetData();
              handleTWNavigation();
              window.scrollTo(0, 0);
            }}
            isSaveDisabled={!isDirty}
            isLoading={tableTabbedProps.isLoading}
            showEditButtons={Boolean(isTimeWindowsScreen || !benchmark?.id)}
            discardText='Cancel'
            isDirty={isDirty}
          />
          <DevTool control={control} placement='top-left' />
        </FormProvider>
      </LoadingWrapper>
    </>
  );
};
